import { EventEmitter } from 'events'
import mqtt from 'mqtt'
import uuid from 'uuid/v4'
import noop from 'modules/noop'

class MqttDispatcher extends EventEmitter {
  constructor () {
    super()
    this.user = null
    this.client = null
    this.connected = false
    this.baseChannel = 'xi/blue/v1/15229523-c3f8-4c10-b4cb-d836a7246cb4/d/'
  }

  setUser (user) {
    if (!user.xivelyUsername || !user.xivelyPassword) {
      return
    }

    this.user = user
    this.client = mqtt.connect({
      port: 443,
      host: 'broker.xively.com',
      protocol: 'wss',
      clean: true,
      keepAlive: 60,
      username: user.xivelyUsername,
      password: user.xivelyPassword
    })

    this.client.on('connect', () => {
      this.emit('connect')
      this.connected = true
    })

    this.client.on('close', () => {
      this.emit('close')
      this.connected = false
    })

    this.client.on('message', (topic, msgBuffer) => {
      const msgString = msgBuffer.toString()

      let message
      try {
        message = JSON.parse(msgString)
      } catch (e) {
        message = msgString
      }

      this.emit(topic, message)
    })
  }

  clearUser () {
    this.user = null
    this.connected = false
    this.removeAllListeners()
    if (this.client) {
      this.client.end().removeAllListeners()
      this.client = null
    }
  }

  isConnected () {
    return this.connected
  }

  subscribe (deviceId, channel, callback) {
    const topic = this.buildFullTopic(deviceId, channel)
    this.on(topic, callback)
    this.client && this.client.subscribe(topic)
  }

  unsubscribe (deviceId, channel, callback) {
    const topic = this.buildFullTopic(deviceId, channel)
    if (callback) {
      this.removeListener(topic, callback)
    } else {
      this.removeAllListeners(topic)
    }

    if (!this.listenerCount(topic)) {
      this.client && this.client.unsubscribe(topic)
    }
  }

  onJobComplete ({ deviceId, jobId }, callback) {
    const channel = `job/${jobId}`
    const handler = (message) => {
      if (message.success) {
        callback(null, message)
      } else {
        callback(message.error || 'Unknown error')
      }
      this.unsubscribe(deviceId, channel, handler)
    }
    this.subscribe(deviceId, channel, handler)
  }

  publish (deviceId, channel, message) {
    if (typeof message === 'object') {
      message = JSON.stringify(message)
    }
    const topic = this.buildFullTopic(deviceId, channel)
    this.client && this.client.publish(topic, message)
  }

  publishControl ({ deviceId, message }, callback) {
    this.publish(deviceId, 'control', message)
    const publishedRegister = message.register

    // After publishing a "control" change, we have to wait for two "written" messages
    // to confirm it worked
    const writtenTopic = this.buildFullTopic(deviceId, 'monitoring')
    let writtenSightings = 0

    const waitForWritten = (message) => {
      if (typeof message === 'object' && message.operation === 'control' &&
                                         message.register === publishedRegister) {
        if (message.success) {
          callback(null)
        } else {
          callback('Error updating control')
        }

        this.removeListener(writtenTopic, waitForWritten)
        clearTimeout(timeoutId)
      } else if (message === 'written') {
        writtenSightings++

        if (writtenSightings >= 2) {
          this.removeListener(writtenTopic, waitForWritten)
          clearTimeout(timeoutId)
          callback(null)
        }
      } else if (message === 'modbus request error - modbus request timed out') {
        this.removeListener(writtenTopic, waitForWritten)
        clearTimeout(timeoutId)
        callback('modbus request timeout')
      }
    }

    const timeoutId = setTimeout(() => {
      this.removeListener(writtenTopic, waitForWritten)
      callback('No response from farm')
    }, 6000)

    this.on(writtenTopic, waitForWritten)
  }

  publishReadController ({ deviceId, message, timeout }, callback) {
    this.publishWithResponse({
      deviceId,
      message,
      timeout,
      requestChannel: 'read_controller',
      responseChannel: 'monitoring'
    }, callback)
  }

  publishConfiguration ({ deviceId, message }, callback) {
    this.publishWithResponse({
      deviceId,
      message,
      requestChannel: 'configure',
      responseChannel: 'monitoring',
      timeout: 10000
    }, callback)
  }

  publishWithResponse (options, callback = noop) {
    let { deviceId, message, requestChannel, responseChannel, timeout = 6000 } = options

    if (!message.id) {
      message = {
        ...message,
        id: uuid()
      }
    }

    const { id } = message

    const waitForResponse = (message) => {
      if (typeof message === 'object' && message.id === id) {
        this.unsubscribe(deviceId, responseChannel, waitForResponse)
        clearTimeout(timeoutId)

        if (message.success) {
          callback(null, message)
        } else {
          callback(message.error || 'Error on CloudGate')
        }
      }
    }

    const timeoutId = setTimeout(() => {
      this.unsubscribe(deviceId, responseChannel, waitForResponse)
      callback('No response from farm')
    }, timeout)

    this.subscribe(deviceId, responseChannel, waitForResponse)
    this.publish(deviceId, requestChannel, message)
  }

  buildFullTopic (deviceId, channel) {
    return this.baseChannel + deviceId + '/' + channel
  }
}

export default new MqttDispatcher()



// WEBPACK FOOTER //
// /Users/alextaxiera/source/farmhand-server/client/dashboard/src/js/modules/mqtt-dispatcher.js